/**
 * Resumen del portal del cliente (`/resumen`).
 *
 * Es el portado de `views/portal_cliente/dashboard.php`: los números del cliente,
 * lo último que consumió, sus comprobantes recientes, lo que tiene pendiente de
 * pago y el consumo de los últimos 6 meses.
 */

import { AlertTriangle, Fuel, LayoutDashboard, Receipt, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';

import { BarChart } from '@/components/charts/bar-chart';
import { ChartFrame } from '@/components/charts/chart-frame';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, SectionCard } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TituloPagina } from '@/components/ui/titulo-pagina';
import { Alert } from '@/components/ui/alert';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import { usePortalDashboard } from '../portal.hooks';
import { selectClientePortal, usePortalStore } from '../portal.store';

/** Etiqueta corta de un mes `AAAA-MM` → `may 26`. */
function etiquetaMes(mes: string): string {
  const [anio, numero] = mes.split('-');
  const nombres = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'set', 'oct', 'nov', 'dic'];
  const indice = Number(numero) - 1;

  return `${nombres[indice] ?? numero} ${String(anio).slice(2)}`;
}

/** Dato grande del resumen. */
function Kpi({
  etiqueta,
  valor,
  detalle,
  tono = 'neutral',
}: {
  etiqueta: string;
  valor: string;
  detalle?: string;
  tono?: 'neutral' | 'marca' | 'aviso';
}) {
  return (
    <div
      className={cn(
        'relative flex min-w-0 flex-col gap-1 overflow-hidden rounded-card border px-4 py-3 shadow-soft',
        tono === 'aviso' && 'border-amber-200 bg-gradient-to-br from-amber-50 to-white',
        tono === 'marca' && 'border-brand-200 bg-gradient-to-br from-brand-50 to-white',
        tono === 'neutral' && 'border-ink-100 bg-white',
      )}
    >
      {/* Filete superior con el color del tono: da identidad a cada indicador. */}
      <span
        aria-hidden="true"
        className={cn(
          'absolute inset-x-0 top-0 h-1 bg-gradient-to-r',
          tono === 'aviso' && 'from-amber-400 to-amber-500',
          tono === 'marca' && 'from-brand-500 to-accent-400',
          tono === 'neutral' && 'from-ink-200 to-ink-100',
        )}
      />
      <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">{etiqueta}</p>
      <p
        className={cn(
          'truncate text-xl font-bold tabular-nums',
          tono === 'marca' && 'text-brand-700',
          tono === 'aviso' && 'text-amber-700',
          tono === 'neutral' && 'text-ink-900',
        )}
        title={valor}
      >
        {valor}
      </p>
      {detalle ? <p className="truncate text-xs text-ink-500">{detalle}</p> : null}
    </div>
  );
}

export function PortalDashboardPage() {
  useDocumentTitle('Portal · Resumen');
  const consulta = usePortalDashboard();
  const cliente = usePortalStore(selectClientePortal);

  if (consulta.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (consulta.error || !consulta.data) {
    return (
      <Alert variant="error" title="No se pudo cargar tu resumen">
        {consulta.error instanceof ApiError ? consulta.error.message : 'Intenta nuevamente.'}
      </Alert>
    );
  }

  const { resumen, lineas, ultimos_consumos: consumos, ultimos_comprobantes: comprobantes, consumos_por_mes: porMes, vencimientos } = consulta.data;
  const conDeuda = resumen.saldo_pendiente > 0;

  return (
    <div className="flex flex-col gap-4">
      <TituloPagina
        icono={LayoutDashboard}
        titulo="Mi resumen"
        descripcion="Tu consumo, tus comprobantes y el estado de tus créditos, en una sola pantalla."
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          etiqueta="Saldo por pagar"
          valor={formatCurrency(resumen.saldo_pendiente)}
          detalle={`${formatNumber(resumen.facturas_pendientes)} documento(s) por pagar`}
          tono={conDeuda ? 'aviso' : 'neutral'}
        />
        <Kpi
          etiqueta="Consumido (6 meses)"
          valor={formatCurrency(resumen.total_consumido)}
          detalle={`${formatNumber(resumen.litros)} galones · ${formatNumber(resumen.consumos)} consumos`}
          tono="marca"
        />
        <Kpi
          etiqueta="Crédito disponible"
          valor={formatCurrency(resumen.disponible)}
          detalle={`${formatNumber(resumen.lineas_credito)} línea(s) · balance ${formatCurrency(resumen.balance)}`}
        />
        <Kpi
          etiqueta="Vales por canjear"
          valor={formatCurrency(resumen.por_canjear)}
          detalle="Vales de consumo activos sin liquidar"
        />
      </div>

      {vencimientos.length > 0 ? (
        <Alert variant={vencimientos.some((item) => item.vencida) ? 'warning' : 'info'}>
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
            <span className="font-semibold">Vencimientos:</span>
            {vencimientos.map((item) => (
              <span key={item.documento} className="inline-flex items-center gap-1">
                <span className="font-mono text-xs">{item.documento}</span>
                <span className="text-xs">
                  {formatDateValue(item.vencimiento)} · {formatCurrency(item.saldo)}
                </span>
                {item.vencida ? <Badge variant="danger">vencida</Badge> : null}
              </span>
            ))}
          </span>
        </Alert>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-3">
        <SectionCard
          className="xl:col-span-2"
          title="Consumo de los últimos 6 meses"
          description="Monto consumido a crédito en cada mes."
        >
          <ChartFrame>
            <BarChart
              labels={porMes.map((item) => etiquetaMes(item.mes))}
              series={[{ label: 'Consumido', data: porMes.map((item) => item.total) }]}
              height={260}
              format="currency"
            />
          </ChartFrame>
        </SectionCard>

        <SectionCard title="Mis líneas de crédito" description="Línea, disponible y balance.">
          {lineas.length === 0 ? (
            <p className="flex items-center gap-2 rounded-control bg-brand-50/70 px-3 py-2 text-sm text-ink-600">
              <Wallet className="size-4 shrink-0 text-brand-500" aria-hidden="true" />
              No tienes líneas de crédito activas.
            </p>
          ) : (
            <ul className="flex flex-col gap-3">
              {lineas.map((linea) => (
                <li key={linea.id} className="rounded-control border border-ink-100 px-3 py-2">
                  <p className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                    <Wallet className="size-3.5 text-brand-500" aria-hidden="true" />
                    {linea.producto}
                    {linea.vencimiento ? (
                      <span className="text-[11px] font-normal text-ink-400">
                        vence {formatDateValue(linea.vencimiento)}
                      </span>
                    ) : null}
                  </p>
                  <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <dt className="text-ink-400">Línea</dt>
                      <dd className="font-medium tabular-nums text-ink-800">
                        {formatCurrency(linea.linea)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-ink-400">Disponible</dt>
                      <dd className="font-medium tabular-nums text-emerald-600">
                        {formatCurrency(linea.disponible)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-ink-400">Balance</dt>
                      <dd className="font-medium tabular-nums text-amber-600">
                        {formatCurrency(linea.balance)}
                      </dd>
                    </div>
                  </dl>
                </li>
              ))}
            </ul>
          )}
          <Link
            to="/creditos"
            className="mt-3 inline-block text-xs font-semibold text-brand-600 hover:underline"
          >
            Ver mis créditos →
          </Link>
        </SectionCard>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <SectionCard
          title="Últimos consumos"
          description="Tus últimas cargas a crédito."
          className="min-w-0"
          action={
            <Link
              to="/consumos"
              className="text-xs font-semibold text-brand-600 hover:underline"
            >
              Ver todos
            </Link>
          }
        >
          {consumos.length === 0 ? (
            <p className="flex items-center gap-2 rounded-control bg-brand-50/70 px-3 py-2 text-sm text-ink-600">
              <Fuel className="size-4 shrink-0 text-brand-500" aria-hidden="true" />
              Todavía no registramos consumos a tu nombre.
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-ink-100">
              {consumos.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-3 py-2">
                  <span className="min-w-0">
                    <span className="flex items-center gap-2 text-sm font-medium text-ink-800">
                      <Fuel className="size-3.5 shrink-0 text-brand-500" aria-hidden="true" />
                      <span className="truncate">{item.producto}</span>
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-ink-500">
                      {formatDateValue(item.fecha)} · {formatNumber(item.cantidad)} gal
                      {item.placa ? ` · ${item.placa}` : ''}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm font-semibold tabular-nums text-ink-900">
                    {formatCurrency(item.total)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard
          title="Comprobantes recientes"
          description="Tus últimas facturas a crédito."
          className="min-w-0"
          action={
            <Link
              to="/comprobantes"
              className="text-xs font-semibold text-brand-600 hover:underline"
            >
              Ver todos
            </Link>
          }
        >
          {comprobantes.length === 0 ? (
            <p className="flex items-center gap-2 rounded-control bg-brand-50/70 px-3 py-2 text-sm text-ink-600">
              <Receipt className="size-4 shrink-0 text-brand-500" aria-hidden="true" />
              No tienes comprobantes emitidos.
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-ink-100">
              {comprobantes.map((item) => (
                <li key={`${item.origen}-${item.id}`} className="flex items-center justify-between gap-3 py-2">
                  <span className="min-w-0">
                    <span className="flex items-center gap-2 text-sm font-medium text-ink-800">
                      <Receipt className="size-3.5 shrink-0 text-brand-500" aria-hidden="true" />
                      <span className="font-mono">{item.documento}</span>
                      <Badge variant={item.estado_pago === 'Vencido' ? 'danger' : 'neutral'}>
                        {item.estado_pago ?? item.estado}
                      </Badge>
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-ink-500">
                      Emitido {formatDateValue(item.fecha)}
                      {item.vencimiento ? ` · vence ${formatDateValue(item.vencimiento)}` : ''}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block text-sm font-semibold tabular-nums text-ink-900">
                      {formatCurrency(item.total)}
                    </span>
                    {item.saldo !== undefined && item.saldo > 0 ? (
                      <span className="block text-[11px] text-amber-600">
                        debe {formatCurrency(item.saldo)}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>¿Necesitas ayuda?</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-ink-600">
            Escribe a la estación indicando tu documento{' '}
            <span className="font-mono">{cliente?.numero_doc}</span> y el comprobante o la fecha del
            consumo en duda: con eso ubicamos tus consumos y saldos.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
